import { gql } from "@apollo/client";
import { Button, Menu, useMenuItems } from "@resource/atlas";
import { atlasEllipsisHorizontal } from "@resource/atlas/icons";
import { useCandidateTimezoneContext } from "client/guide-content/rich-blocks/candidate-timezone-provider";
import clsx from "clsx";
import SafeHtml from "components/Generic/SafeHtml";
import { Skeleton, SkeletonCollection } from "components/Skeleton";
import { InterviewItemCardInterviewFragment } from "generated/schemaTypes";
import { ComponentPropsWithoutRef, useState } from "react";

import { CalendarIcon } from "../CalendarIcon";
import { InterviewTitle } from "../InterviewTitle/InterviewTitle";
import {
  useFormattedDateTimesForInterviewItem,
  useReplacedTitleAndDescriptionForInterviewItem,
} from "../utils";
import { InProgressCounter } from "./InProgressCounter";
import { InterviewersSection } from "./InterviewersSection";
import { LocationSection } from "./LocationSection";
import { StartCountdown } from "./StartCountdown";

const INTERVIEW_FRAGMENT = gql`
  fragment InterviewItemCardInterviewFragment on Event {
    id
    replacementData
    title: displayTitle
    description: displayDescription
    startTime
    prominentInstructions {
      id
      url: label
      label
    }
    hidden: displayHidden
    endTime
    conferenceUrl
    interviewers {
      ...InterviewItemCardInterviewerFragment
    }
    ...InterviewItemCardLocationInterviewFragment
  }
  ${InterviewersSection.fragments.interviewer}
  ${LocationSection.fragments.interview}
`;

export function InterviewItemLoading() {
  return (
    <div className="p-6 shadow-1 rounded-md w-full relative">
      <div className="w-[52px] h-[52px] shadow-1 rounded-md absolute top-6 right-6" />
      <div className="space-y-3">
        <Skeleton className="w-1/2 h-[14px]" type="text" />
        <Skeleton className="w-2/3 h-[14px]" type="text" />
        <Skeleton className="w-3/4 h-[14px]" type="text" />
      </div>
      <div className="mt-5 flex flex-row items-center">
        <Skeleton className="w-5 h-5 mr-3" type="rect" />
        <Skeleton className="w-[104px] h-[14px]" type="text" />
      </div>
      <div className="mt-4 flex flex-row items-center">
        <SkeletonCollection
          className="flex flex-row-reverse mr-[6px]"
          count={2}
        >
          {(i) => (
            <Skeleton
              className={clsx("w-8 h-8", {
                "ring-white ring-2 -mr-1": i !== 0,
              })}
              type="circle"
            />
          )}
        </SkeletonCollection>
        <Skeleton className="w-[104px] h-[14px]" type="text" />
      </div>
    </div>
  );
}

export type InterviewItemCardProps = ComponentPropsWithoutRef<"div"> & {
  interview: InterviewItemCardInterviewFragment;
  hideStartTimeNotification?: boolean;
  menuItems?:
    | ReturnType<typeof useMenuItems>
    | ((interviewId: string) => ReturnType<typeof useMenuItems>);
};

export function InterviewItemCard({
  interview,
  hideStartTimeNotification,
  className,
  menuItems,
  ...props
}: InterviewItemCardProps) {
  const { timezone } = useCandidateTimezoneContext();
  const formattedDateTime = useFormattedDateTimesForInterviewItem({
    interview,
    hideStartTimeNotification,
    timezone,
  });

  const replacedTitleAndDescription =
    useReplacedTitleAndDescriptionForInterviewItem({ interview });

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div
      className={clsx("p-4 w-full relative group/interview", className)}
      {...props}
    >
      {menuItems && (
        <Menu.Root setOpen={(open) => setIsMenuOpen(open)}>
          <Menu.Trigger>
            <Button
              icon={atlasEllipsisHorizontal}
              size="xs"
              className={clsx(
                "!absolute top-[0.625rem] right-[0.625rem] pointer-events-auto opacity-0 group-hover:opacity-100",
                isMenuOpen && "opacity-100"
              )}
              isActive={isMenuOpen}
            />
          </Menu.Trigger>
          <Menu.Content
            portal
            items={
              Array.isArray(menuItems) ? menuItems : menuItems(interview.id)
            }
          />
        </Menu.Root>
      )}
      <div className="flex flex-col gap-3 md:gap-4">
        <header className="flex gap-3">
          <div className="flex-grow truncate">
            <div className="space-y-1 md:space-y-1 text-start">
              {!formattedDateTime.isInPast && (
                <>
                  {formattedDateTime.startsIn && (
                    <StartCountdown startsIn={formattedDateTime.startsIn} />
                  )}
                  <InProgressCounter
                    startTime={formattedDateTime.rawStartTime}
                    endTime={formattedDateTime.rawEndTime}
                  />
                </>
              )}
              <div className="flex flex-col gap-1">
                <p className="text-subtle text-body-sm md:text-body-md">
                  {formattedDateTime.formattedStartDate} •{" "}
                  {formattedDateTime.formattedTime}
                </p>
                <InterviewTitle
                  interview={interview}
                  className={clsx(
                    "text-body-md-heavy md:text-body-lg-heavy",
                    formattedDateTime.isInPast ? "text-subtle" : "text-dark"
                  )}
                />
                {interview.description && (
                  <SafeHtml
                    isTextPreview
                    html={replacedTitleAndDescription.description}
                    className={clsx(
                      "truncate w-full text-body-md",
                      formattedDateTime.isInPast ? "text-subtle" : "text-dark"
                    )}
                    allowedTags={[]}
                  />
                )}
              </div>
            </div>
          </div>
          <CalendarIcon
            className="flex-shrink-0"
            isInPast={formattedDateTime.isInPast}
            day={formattedDateTime.day}
            dayName={formattedDateTime.dayName}
          />
        </header>
        <LocationSection
          interview={interview}
          isInPast={formattedDateTime.isInPast}
        />
        <InterviewersSection
          interviewers={interview.interviewers}
          isInPast={formattedDateTime.isInPast}
        />
      </div>
    </div>
  );
}

InterviewItemCard.fragments = {
  interview: INTERVIEW_FRAGMENT,
};
